import { cleanSearchParams, requestData } from "../api-config";

import { serializePatientSubmissionFilters } from "@/app/[tenant]/(console)/check-in/table-filter-contracts";

import { getTimezone } from "@/lib/timezone";
import { toast } from "@/lib/use-toast";

import {
  FormStructure,
  FormSubmissionFetchParams,
  PatientSubmission,
} from "@/types/forms/answered-form";
import {
  ConsentDocument,
  ConsentText,
  FormDetail,
  FormKind,
  FormMembershipContext,
  FormTable,
  FormTiny,
  OpportunityFormSubmission,
  PatientSubmissionsFetchParams,
  PatientSubmissionsTable,
  ReplaceFormsResult,
  ReplacedForm,
  VariableCatalogEntry,
} from "@/types/forms/form";
import { NoteTemplateTableItem } from "@/types/notes";
import { Page } from "@/types/pagination";

export interface ConvertFormToTemplateRequest {
  name?: string;
  userIds?: string[];
}

export async function convertFormToNoteTemplate(
  formId: string,
  options?: ConvertFormToTemplateRequest,
): Promise<NoteTemplateTableItem | null> {
  return await requestData<NoteTemplateTableItem>(
    `notes/templates/from-form/${formId}`,
    "post",
    options ? { json: options } : undefined,
    false, // Don't catch errors - let them propagate for proper error handling
  );
}

/** Lists clinical forms unless `kinds` asks for others too. */
export async function getFormTable(
  includeArchived?: boolean,
  kinds?: FormKind[],
): Promise<FormTable[]> {
  const searchParams = new URLSearchParams();
  if (includeArchived) searchParams.set("include_archived", "true");
  for (const kind of kinds ?? []) searchParams.append("kinds", kind);
  try {
    const forms = await requestData<FormTable[]>(
      "forms/table",
      "get",
      { searchParams },
      false,
    );
    if (forms === null) throw new Error("Active forms could not be loaded");
    return forms;
  } catch (error) {
    toast({
      id: "forms-load-error",
      title: "Failed to load forms",
      description: "Please try again.",
      variant: "error",
    });
    throw error;
  }
}

export async function archiveForm(id: string): Promise<boolean | null> {
  return await requestData<boolean>(`forms/archive/${id}`, "delete");
}

/** Variables the form builder can insert into form text. */
export async function getVariableCatalog(): Promise<
  VariableCatalogEntry[] | null
> {
  return await requestData<VariableCatalogEntry[]>(
    "forms/variable-catalog",
    "get",
  );
}

/**
 * Whether this form's text renders a membership plan's numbers, and whether any
 * plan uses it as its enrollment form. Decided server-side because `FormTable` —
 * what the surfaces offering this form actually hold — carries no blocks to
 * inspect.
 */
export async function getFormMembershipContext(
  formId: string,
): Promise<FormMembershipContext | null> {
  // catchError=false so a failed probe rejects instead of resolving to null. Null is
  // indistinguishable from uses_membership_variables=false, which is the answer that
  // lets a fill or send proceed with no plan — the silent wrong price this whole
  // feature exists to prevent. The gate has to be able to see the failure.
  return await requestData<FormMembershipContext>(
    `forms/${formId}/membership-context`,
    "get",
    undefined,
    false,
  );
}

export async function archiveSubmission(
  answeredFormId: string,
): Promise<boolean | null> {
  // catchError=false so a failed archive (e.g. the submission completed
  // concurrently -> 400) rejects and the mutation can roll back its optimistic
  // update, rather than resolving to null and looking like a success.
  return await requestData<boolean>(
    `forms/submissions/${answeredFormId}`,
    "delete",
    undefined,
    false,
  );
}

export interface RefillFormsResult {
  archivedSubmissionIds: string[];
  reopenedEventIds: string[];
}

/** Sets aside the patient's current copy of these forms so they can be filled
 *  again, and reopens the forms step on `eventId` and upcoming visits. */
export async function refillForms(payload: {
  patientId: string;
  formIds: string[];
  eventId?: string;
}): Promise<RefillFormsResult | null> {
  return await requestData<RefillFormsResult>(
    "forms/submissions/refill",
    "post",
    { json: payload },
    false,
  );
}

export async function bulkArchiveForms(ids: string[]): Promise<boolean | null> {
  return await requestData<boolean>("forms/bulk-archive", "delete", {
    json: { ids },
  });
}

export interface MergeFormsRequest {
  sourceFormId: string;
  targetFormId: string;
}

export async function mergeForms(
  request: MergeFormsRequest,
): Promise<boolean | null> {
  return await requestData<boolean>("forms/merge", "post", {
    json: {
      source_form_id: request.sourceFormId,
      target_form_id: request.targetFormId,
    },
  });
}

export interface ReplaceFormsRequest {
  formId: string;
  replacedFormIds: string[];
  /** Patients who completed the replaced forms don't need to fill this one out. */
  carryOverCompletions: boolean;
  /** With carry-over: the replaced forms are versions of this one, so any one is enough. */
  replacedAreVersions: boolean;
  archiveReplaced: boolean;
}

/** Clinical forms' ids and names, for pickers. */
export async function getFormOptions(
  includeArchived: boolean,
): Promise<FormTiny[] | null> {
  return await requestData<FormTiny[]>(
    "forms/options",
    "get",
    { searchParams: { include_archived: String(includeArchived) } },
    false,
  );
}

export async function previewReplaceForms(
  formId: string,
  replacedFormIds: string[],
): Promise<ReplaceFormsResult | null> {
  const searchParams = new URLSearchParams();
  replacedFormIds.forEach((id) => searchParams.append("replaced_form_ids", id));
  return await requestData<ReplaceFormsResult>(
    `forms/${formId}/replace-preview`,
    "get",
    { searchParams },
    false,
  );
}

export async function replaceForms(
  request: ReplaceFormsRequest,
): Promise<ReplaceFormsResult | null> {
  return await requestData<ReplaceFormsResult>(
    `forms/${request.formId}/replace`,
    "post",
    {
      json: {
        replaced_form_ids: request.replacedFormIds,
        carry_over_completions: request.carryOverCompletions,
        replaced_are_versions: request.replacedAreVersions,
        archive_replaced: request.archiveReplaced,
      },
    },
    false,
  );
}

export async function getReplacedForms(
  formId: string,
): Promise<ReplacedForm[] | null> {
  return await requestData<ReplacedForm[]>(
    `forms/${formId}/replaced-forms`,
    "get",
  );
}

export async function removeReplacedForm(
  formId: string,
  replacedFormId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `forms/${formId}/replaced-forms/${replacedFormId}`,
    "delete",
    undefined,
    false,
  );
}

export async function getFormStructure(
  formId: string,
  answeredFormId?: string,
): Promise<FormStructure | null> {
  return await requestData<FormStructure>(
    `forms/${formId}/structure`,
    "get",
    answeredFormId
      ? { searchParams: { answered_form_id: answeredFormId } }
      : undefined,
  );
}

export function serializeFormSubmissionFilters(
  params: FormSubmissionFetchParams,
  tz: string,
): URLSearchParams {
  const { submissionDate, ...rest } = params;
  const query = cleanSearchParams({ ...rest, tz }) as URLSearchParams;
  submissionDate?.forEach((bound) =>
    query.append("submission_date", bound ?? ""),
  );
  return query;
}

export async function getFormSubmissions(
  formId: string,
  params: FormSubmissionFetchParams,
  controller?: AbortController,
): Promise<Page<PatientSubmission> | null> {
  const tz = getTimezone();
  return await requestData<Page<PatientSubmission>>(
    `forms/${formId}/submissions`,
    "get",
    {
      searchParams: serializeFormSubmissionFilters(params, tz),
      signal: controller?.signal,
    },
  );
}

export async function getPatientSubmissionTable(
  params: PatientSubmissionsFetchParams,
  controller?: AbortController,
): Promise<PatientSubmissionsTable | null> {
  const tz = getTimezone();
  return await requestData<PatientSubmissionsTable>(
    `forms/submissions`,
    "get",
    {
      searchParams: serializePatientSubmissionFilters({ ...params, tz }),
      signal: controller?.signal,
    },
  );
}

export async function getSignedUrlBatch(
  file_paths: string[],
): Promise<Record<string, string> | null> {
  return await requestData<Record<string, string>>(
    "forms/batch-signed-uri",
    "get",
    {
      searchParams: {
        file_paths: file_paths.join(","),
      },
    },
  );
}

export async function getConsentInfo(
  formId: string,
  answeredFormId?: string,
): Promise<(ConsentText | ConsentDocument)[] | null> {
  return await requestData<(ConsentText | ConsentDocument)[]>(
    `forms/${formId}/consent-info`,
    "get",
    answeredFormId
      ? { searchParams: { answered_form_id: answeredFormId } }
      : undefined,
  );
}

export async function updateFormDefaultText(
  formId: string,
  defaultText: string,
): Promise<boolean | null> {
  return await requestData<boolean>(`forms/${formId}/default-text`, "put", {
    json: {
      form_id: formId,
      default_text: defaultText,
    },
  });
}

export async function createForm(
  formData: FormDetail,
): Promise<FormDetail | null> {
  return await requestData<FormDetail>(
    "forms",
    "post",
    { json: formData },
    false,
  );
}

/** `membershipId` prices a blank agreement for one plan — for printing it, and nothing
 *  else. The form editor reads this same route and saves the same shape back, so passing
 *  a plan from there would bake that plan's figures into the shared template. */
export async function getFormDetails(
  formId: string,
  membershipId?: string,
): Promise<FormDetail | null> {
  return await requestData<FormDetail>(
    `forms/${formId}/details`,
    "get",
    membershipId
      ? { searchParams: { membership_id: membershipId } }
      : undefined,
  );
}

export async function updateFormExpiryDays(
  formId: string,
  expiryDays: number | null,
): Promise<boolean | null> {
  return await requestData<boolean>(`forms/${formId}/expiry-days`, "put", {
    json: {
      form_id: formId,
      expiry_days: expiryDays,
    },
  });
}

export async function updateForm(
  formId: string,
  formData: FormDetail,
): Promise<FormDetail | null> {
  return await requestData<FormDetail>(
    `forms/${formId}`,
    "put",
    { json: formData },
    false,
  );
}

/**
 * Record that staff sent, linked, or handed over these forms for this patient.
 *
 * A form whose text names `{provider.*}` renders the sender's details, and nothing else
 * on these surfaces reaches the API with a provider in scope — an SMS send goes out as
 * a chat draft carrying no form id, and Copy Form Link issues no request at all. Without
 * this the patient opens the form and it refuses.
 *
 * Deliberately not awaited by the send itself: the form has still been sent if this
 * fails, so blocking the dialog on it would be worse than a form that refuses.
 *
 * `catchError: false` so a failure actually rejects. The default swallows it and
 * resolves `null`, which made every caller's `.catch()` unreachable — the staff-facing
 * "sent, but the sender was not recorded" toasts were dead code, and a permission or
 * network failure here reached nobody until the patient hit Form Not Ready days later.
 * Not awaiting is the deliberate part; not hearing about it was not.
 *
 * `sendFormEmail` records its own, so an email send never calls this.
 */
export async function recordFormAssignment(
  patientId: string,
  formIds: string[],
): Promise<boolean | null> {
  return await requestData<boolean>(
    `forms/assignment`,
    "post",
    { json: { patientId, formIds } },
    false,
  );
}

export interface SendFormEmailPayload {
  patientId: string;
  formIds: string[];
  bodyHtml: string;
  subject?: string;
}

export async function sendFormEmail(
  payload: SendFormEmailPayload,
): Promise<boolean | null> {
  return await requestData<boolean>(`forms/email`, "post", {
    json: payload,
  });
}

export async function getOpportunityFormSubmissions(
  opportunityId: string,
): Promise<OpportunityFormSubmission[] | null> {
  return await requestData<OpportunityFormSubmission[]>(
    `forms/opportunity/${opportunityId}/submissions`,
    "get",
  );
}
